/**
 * LumaJSON Panel
 *
 * Displays the raw LumaJSON data for any artifact type.
 * Shows formatted, syntax-highlighted JSON that will be exported to Luma.
 * Read-only viewer with copy-to-clipboard functionality.
 */

import React, { useState } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { Button } from '../ui/Button';
import { Copy, Check, FileJson, Download } from 'lucide-react';

export const LumaJsonPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const [copied, setCopied] = useState(false);

  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = selectedArtifactId && project ? project.artifacts[selectedArtifactId] : null;
  const lumaJson = artifact?.lumaJson;

  const handleCopy = () => {
    if (lumaJson) {
      const jsonString = typeof lumaJson === 'string' ? lumaJson : JSON.stringify(lumaJson, null, 2);
      navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (lumaJson && artifact) {
      const jsonString = typeof lumaJson === 'string' ? lumaJson : JSON.stringify(lumaJson, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${artifact.identifier || 'artifact'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Empty state when no LumaJSON exists
  if (!lumaJson) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <FileJson className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
          No LumaJSON Generated Yet
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">
          Use the "To LumaJSON" button in the LumaScript tab to generate the final JSON that will be exported to Luma.
        </p>
      </div>
    );
  }

  // Format JSON for display
  const jsonString = typeof lumaJson === 'string' ? lumaJson : JSON.stringify(lumaJson, null, 2);

  return (
    <div className="w-full h-full flex flex-col bg-background">
      {/* Header Bar */}
      <div className="h-12 border-b border-border bg-background flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <FileJson className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">{artifact?.name || 'Artifact'} - LumaJSON</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCopy}
            className="flex items-center gap-1.5"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDownload}
            className="flex items-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </Button>
        </div>
      </div>

      {/* JSON Content */}
      <div className="flex-1 overflow-auto p-4">
        <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-900 border border-border rounded-lg p-4 overflow-x-auto">
          <code className="text-foreground">{jsonString}</code>
        </pre>
      </div>
    </div>
  );
};
